/**
 * Single source of truth for the pipe-run / lateral **backfill type** used across
 * the Road & Sewers Foul and Storm take-off tabs and their bill summaries.
 *
 * Keep all backfill values, display labels, evaluation codes and Excel-import
 * normalization here so a future rename only touches one file.
 *
 * SCOPE: This is Road & Sewers specific. Groundworks intentionally keeps its own
 * "Type 1" label (e.g. sub-base material) and must NOT consume these constants.
 */

/** Canonical backfill values persisted on pipe runs / laterals. */
export const BACKFILL_TYPE = {
  MOT_TYPE_1: 'motType1',
  RC_6F2: '6F2',
  Q_6F5: '6F5',
  AS_DUG: 'asDug',
} as const;

export type BackfillType = (typeof BACKFILL_TYPE)[keyof typeof BACKFILL_TYPE];

/** Options rendered in the Backfill `<Select>` on the Foul/Storm take-off tables. */
export const BACKFILL_TYPE_OPTIONS: ReadonlyArray<{ value: BackfillType; label: string }> = [
  { value: BACKFILL_TYPE.MOT_TYPE_1, label: 'Mot Type 1' },
  { value: BACKFILL_TYPE.RC_6F2, label: 'R/C 6F2' },
  { value: BACKFILL_TYPE.Q_6F5, label: 'Q 6F5' },
  { value: BACKFILL_TYPE.AS_DUG, label: 'As Dug' },
];

/**
 * Migrate a legacy backfill value to its current canonical form.
 *
 * Projects saved before the "Type 1" → "Mot Type 1" rename stored the value as
 * `type1`; map it to `motType1` so old data still renders and selects correctly.
 */
export const migrateBackfillType = (bt: string | undefined | null): string => {
  if (bt === 'type1') return BACKFILL_TYPE.MOT_TYPE_1;
  return bt ?? '';
};

/** Full display label for a backfill value (accepts the legacy `type1` value too). */
export const backfillLabel = (bt: string): string => {
  const m: Record<string, string> = {
    [BACKFILL_TYPE.MOT_TYPE_1]: 'Mot Type 1',
    type1: 'Mot Type 1', // legacy value
    [BACKFILL_TYPE.RC_6F2]: 'R/C 6F2',
    [BACKFILL_TYPE.Q_6F5]: 'Q 6F5',
    [BACKFILL_TYPE.AS_DUG]: 'As Dug',
  };
  return m[bt] || bt;
};

/**
 * Short code embedded in the summary "evaluation" strings (e.g. `PS150\T1\...`).
 * This is an internal pricing reference code, not a user-facing label, so it is
 * intentionally left as `T1` for Mot Type 1 to avoid changing existing outputs.
 */
export const backfillEvalCode = (bt: string): string => {
  const m: Record<string, string> = {
    [BACKFILL_TYPE.MOT_TYPE_1]: 'T1',
    type1: 'T1', // legacy value
    [BACKFILL_TYPE.RC_6F2]: '6F2',
    [BACKFILL_TYPE.Q_6F5]: '6F5',
    [BACKFILL_TYPE.AS_DUG]: 'AD',
  };
  return m[bt] || bt;
};

/**
 * Normalize a free-text backfill value (e.g. a cell from an imported Excel
 * spreadsheet) to a canonical {@link BackfillType}. Defaults to Mot Type 1.
 */
export const normalizeBackfillType = (v: unknown): BackfillType => {
  const s = String(v ?? '')
    .toLowerCase()
    .trim();
  if (s.includes('type 1') || s.includes('type1') || s.includes('mot')) return BACKFILL_TYPE.MOT_TYPE_1;
  if (s.includes('6f5')) return BACKFILL_TYPE.Q_6F5;
  if (s.includes('6f2')) return BACKFILL_TYPE.RC_6F2;
  if (s.includes('as dug') || s.includes('asdug')) return BACKFILL_TYPE.AS_DUG;
  return BACKFILL_TYPE.MOT_TYPE_1;
};
